import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import StatCard from '../components/StatCard';
import { LiveMap } from '../components/LiveMap';
import VehicleList from '../components/VehicleList';
import SOSVideoViewer from '../components/SOSVideoViewer';
import { connectCentralWS, disconnectCentralWS, sendWS } from '../services/websocket';
import api from '../services/api';

export default function Dashboard() {
  const navigate = useNavigate();
  const { live } = useSelector((s) => s.vehicles);
  const { pending } = useSelector((s) => s.alerts);
  const [stats, setStats] = useState({});
  const [showStats, setShowStats] = useState(true);
  const [showPanel, setShowPanel] = useState(true);
  const [showVehicles, setShowVehicles] = useState(true);
  const [showSigns, setShowSigns] = useState(true);
  const [showAlerts, setShowAlerts] = useState(true);
  const [focusVehicleId, setFocusVehicleId] = useState(null);
  const [signs, setSigns] = useState([]);
  const [alertsList, setAlertsList] = useState([]);

  useEffect(() => {
    connectCentralWS();
    api.get('/dashboard/').then((r) => setStats(r.data)).catch(() => {});
    api.get('/signs/').then((r) => setSigns(r.data.results || r.data)).catch(() => {});
    api.get('/alerts/?status=active').then((r) => setAlertsList(r.data.results || r.data)).catch(() => {});

    const interval = setInterval(() => {
      api.get('/dashboard/').then((r) => setStats(r.data)).catch(() => {});
      api.get('/alerts/?status=active').then((r) => setAlertsList(r.data.results || r.data)).catch(() => {});
    }, 30000);

    return () => {
      clearInterval(interval);
      disconnectCentralWS();
    };
  }, []);

  const activeCount = Object.values(live).filter((v) => v.status === 'active' || v.status === 'sos').length;
  const sosCount = Object.values(live).filter((v) => v.status === 'sos').length;

  const handleLocate = (vehicleId) => {
    setFocusVehicleId(null);
    setTimeout(() => setFocusVehicleId(vehicleId), 50);
  };

  return (
    <div className="dashboard-fullmap">
      {/* Full-screen map */}
      <LiveMap
        vehicles={live}
        signs={signs}
        alerts={alertsList}
        showVehicles={showVehicles}
        showSigns={showSigns}
        showAlerts={showAlerts}
        focusVehicleId={focusVehicleId}
        onVehicleClick={(id) => navigate(`/vehicle/${id}`)}
      />

      {/* Map layer toggles — floating FAB bar bottom center */}
      <div className="map-toolbar">
        <button className={`map-toggle-btn${showVehicles ? ' active' : ''}`} onClick={() => setShowVehicles(!showVehicles)}>
          <i className="bi bi-truck"></i>
          <span className="fab-label">Véhicules</span>
        </button>
        <button className={`map-toggle-btn${showSigns ? ' active' : ''}`} onClick={() => setShowSigns(!showSigns)}>
          <i className="bi bi-sign-stop"></i>
          <span className="fab-label">Panneaux</span>
        </button>
        <button className={`map-toggle-btn${showAlerts ? ' active' : ''}`} onClick={() => setShowAlerts(!showAlerts)}>
          <i className="bi bi-exclamation-triangle"></i>
          <span className="fab-label">Alertes</span>
        </button>
        <div className="map-toolbar-divider" />
        <button className={`map-toggle-btn${showStats ? ' active' : ''}`} onClick={() => setShowStats(!showStats)}>
          <i className="bi bi-graph-up"></i>
          <span className="fab-label">Stats</span>
        </button>
        <button className={`map-toggle-btn${showPanel ? ' active' : ''}`} onClick={() => setShowPanel(!showPanel)}>
          <i className="bi bi-layout-sidebar-reverse"></i>
          <span className="fab-label">Panel</span>
        </button>
      </div>

      {/* Floating stats bar — top */}
      {showStats && (
        <div className="floating-stats">
          <StatCard icon="bi-truck" label="Actifs" value={activeCount || stats.active_vehicles || 0} />
          <StatCard icon="bi-exclamation-triangle" label="SOS" value={sosCount || stats.sos_active || 0} color="#ff1744" />
          <StatCard icon="bi-bell" label="Alertes" value={pending.length || stats.pending_alerts || 0} color="#ff9100" />
          <StatCard icon="bi-shield-slash" label="Infractions" value={stats.infractions_today || 0} color="#ffea00" />
          <StatCard icon="bi-speedometer" label="Vit. moy." value={`${stats.avg_fleet_speed || 0}`} color="#2979ff" />
          <StatCard icon="bi-car-front" label="Crashes" value={stats.crashes_today || 0} color="#d500f9" />
        </div>
      )}

      {/* Floating side panel — vehicles + message + SOS video */}
      {showPanel && (
        <div className="floating-panel">
          <div className="fp-section">
            <h6 className="fp-title">
              <i className="bi bi-broadcast"></i> Véhicules en ligne
              <span className="fp-count">{activeCount}</span>
            </h6>
            <VehicleList onSelect={(id) => navigate(`/vehicle/${id}`)} onLocate={handleLocate} />
          </div>
          <SOSVideoViewer />
        </div>
      )}
      {showPanel && (
        <div className="floating-panel-fp">
          <div className="fp-section">
            <h6 className="fp-title" style={{ color: '#2979ff' }}>
              <i className="bi bi-megaphone"></i> Message à la flotte
            </h6>
            <MessageSender />
          </div>
        </div>
      )}
    </div>
  );
}

function MessageSender() {
  const [text, setText] = useState('');
  const [priority, setPriority] = useState('normal');

  const handleSend = () => {
    if (!text.trim()) return;
    sendWS('message.send', { text, priority, target_type: 'all' });
    setText('');
  };

  return (
    <div>
      <textarea
        className="form-control form-control-sm mb-2"
        rows={2}
        placeholder="Message..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        style={{ background: '#0d0d1a', border: '1px solid #333', color: '#fff', fontSize: 13 }}
      />
      <div className="d-flex gap-2">
        <select
          className="form-select form-select-sm"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          style={{ background: '#0d0d1a', border: '1px solid #333', color: '#fff', flex: 1, fontSize: 12 }}
        >
          <option value="low">Basse</option>
          <option value="normal">Normale</option>
          <option value="high">Haute</option>
          <option value="urgent">Urgente</option>
        </select>
        <button className="btn btn-sm" onClick={handleSend} style={{ background: '#2979ff', color: '#fff' }}>
          <i className="bi bi-send"></i> Envoyer
        </button>
      </div>
    </div>
  );
}
